import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ClipType } from "@air/api/types";
import { getClipAspectRatio } from "@air/utils-clips";
import { memo, useCallback, useMemo, useState } from "react";
import { buildURL } from "react-imgix";
import { useSelector } from "react-redux";
import { AssetGalleryCardShortInfo } from "~/components/Gallery/AssetGalleryCard/AssetGalleryCardShortInfo";
import { ClipThumbnail } from "~/components/Shared/ClipList/ClipItem/ClipThumbail";
import { VideoScrubbableThumbnail } from "~/components/VideoScrubbableThumbnail";
import { useThumbnailPreference } from "~/hooks/useThumbnailPreference";
import { useVideoScrubbableThumbnail } from "~/hooks/useVideoScrubbableThumbnail";
import { CentralizedBoardSelectors } from "~/store/centralizedBoard/selectors";
import { ClipIconSize } from "~/types/clip-icon-size";
import { DEFAULT_THUMBNAIL_DIMENSIONS } from "./dimensions";
export const AssetCellThumbnail = /*#__PURE__*/ memo((param)=>{
    let { clip, description } = param;
    const [isHovered, setIsHovered] = useState(false);
    const { getPreference } = useThumbnailPreference();
    const boardId = useSelector(CentralizedBoardSelectors.boardId);
    const thumbnailPreference = useMemo(()=>getPreference(boardId), [
        boardId,
        getPreference
    ]);
    const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } = useVideoScrubbableThumbnail({
        aspectRatio: getClipAspectRatio(clip),
        containerDimensions: DEFAULT_THUMBNAIL_DIMENSIONS
    });
    const handleMouseEnter = useCallback(()=>setIsHovered(true), []);
    const handleMouseLeave = useCallback(()=>{
        setIsHovered(false);
        onMouseLeave();
    }, [
        onMouseLeave
    ]);
    const spriteSheetUrl = useMemo(()=>clip.type === ClipType.video && !!clip.assets.spriteSheet ? buildURL(clip.assets.spriteSheet, {
            w: DEFAULT_THUMBNAIL_DIMENSIONS.width * 10,
            dpr: window.devicePixelRatio
        }) : undefined, [
        clip
    ]);
    const videoScrubbableThumbnail = useMemo(()=>!!spriteSheetUrl && isHovered ? /*#__PURE__*/ _jsx(VideoScrubbableThumbnail, {
            backgroundImageRef: backgroundImageRef,
            indicatorRef: indicatorRef,
            src: spriteSheetUrl,
            style: backgroundImageStyles
        }) : null, [
        spriteSheetUrl,
        isHovered,
        backgroundImageRef,
        indicatorRef,
        backgroundImageStyles
    ]);
    const videoDuration = useMemo(()=>isHovered ? null : /*#__PURE__*/ _jsx("div", {
            className: "absolute bottom-1 right-1 z-10",
            children: /*#__PURE__*/ _jsx(AssetGalleryCardShortInfo, {
                clip: clip
            })
        }), [
        clip,
        isHovered
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onMouseMove: onMouseMove,
        children: [
            /*#__PURE__*/ _jsx(ClipThumbnail, {
                ext: clip.ext,
                status: clip.status,
                type: clip.type,
                borderRadius: 4,
                description: description,
                src: clip.assets.image,
                iconSize: ClipIconSize.small,
                hideText: true,
                style: {
                    padding: 0
                },
                thumbnailPreference: thumbnailPreference,
                ...DEFAULT_THUMBNAIL_DIMENSIONS
            }),
            videoScrubbableThumbnail,
            videoDuration
        ]
    });
});
AssetCellThumbnail.displayName = "AssetCellThumbnail";
